// SourceManager.jsx - Composant principal simplifié avec préférences utilisateur
import React, { useState, useEffect, useRef } from 'react';
import { 
  Typography, 
  CircularProgress, 
  Alert, 
  Button,
  Tabs,
  Tab,
  Box
} from '@mui/material';
import {
  ArrowLeft,
  ChevronRight,
  Filter,
  Globe
} from 'lucide-react';
  
import { getAuth } from 'firebase/auth';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import SourceForm from './SourceForm';
import SourceResults from './SourceResults';
import { useSourceSearch } from './useSourceSearch';
import { 
  GeographicAnalysisDisplay, 
  SemanticAnalysisDisplay, 
  RecoveryInfoDisplay,
  ObjectiveHeader,
  harmonizedStyles,
  tabStyle 
} from './SourceDisplays';
import InspirationBreadcrumb from '../CP-Inspiration/InspirationBreadCrumb';

/**
 * Composant principal pour la recherche et la gestion des sources
 */
export default function SourceManager({ propCompetenceId }) {
  // Hooks et états de base
  const { competenceId: urlCompetenceId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const competenceId = propCompetenceId || urlCompetenceId;
  const auth = getAuth();
  const [token, setToken] = useState(null);
  const [activeTab, setActiveTab] = useState(0);
  const [semanticAnalysis, setSemanticAnalysis] = useState(null);
  const [suggestedQuery, setSuggestedQuery] = useState('');
  const [semanticDataReceived, setSemanticDataReceived] = useState(false);
  const searchFormRef = useRef(null);

  // Utilisation du hook personnalisé pour la logique de recherche
  const {
    competence,
    loading,
    error,
    competenceError,
    sources,
    savedSources,
    metadata,
    loadingSaved,
    searchParams,
    geographicAnalysis,
    autoConfigApplied,
    geoAnalysisLoading,
    userPreferences,
    preferencesLoaded,
    providerRecommendations,
    handleSearch,
    handleModeChange,
    handleDeleteSavedSource,
    handleApplyGeographicConfig,
    fetchSavedSources,
    fetchCompetence,
    setProviderRecommendations,
    setError
  } = useSourceSearch(competenceId, token, semanticAnalysis, suggestedQuery);

  // Gestion des données sémantiques depuis la navigation
  useEffect(() => {
    if (location.state && !semanticDataReceived) {
      console.log("Vérification des données d'analyse sémantique reçues:", location.state);
      
      if (location.state.semanticAnalysis) {
        console.log("Analyse sémantique reçue:", location.state.semanticAnalysis);
        setSemanticAnalysis(location.state.semanticAnalysis);
        
        if (location.state.suggestedQuery) {
          console.log("Requête suggérée reçue:", location.state.suggestedQuery);
          setSuggestedQuery(location.state.suggestedQuery);
        } else if (location.state.optimizedQuery) {
          console.log("Requête optimisée reçue:", location.state.optimizedQuery);
          setSuggestedQuery(location.state.optimizedQuery);
        }
        
        setSemanticDataReceived(true);
      }
    }
  }, [location.state, semanticDataReceived]);

  // Gestion du token d'authentification
  useEffect(() => {
    const getToken = async () => {
      try {
        const user = auth.currentUser;
        if (user) {
          const idToken = await user.getIdToken(true);
          setToken(idToken);
        }
      } catch (err) {
        console.error('Erreur lors de l\'obtention du token:', err);
        setError('Erreur d\'authentification. Veuillez vous reconnecter.');
      }
    };
    
    getToken();
  }, [auth, setError]);

  // Validation de l'ID de compétence
  useEffect(() => {
    if (!competenceId) {
      console.error('Aucun ID de compétence fourni');
    }
  }, [competenceId]);

  // Récupération de la compétence
  useEffect(() => {
    if (competenceId && auth.currentUser && token) {
      fetchCompetence();
    }
  }, [competenceId, auth.currentUser, token, fetchCompetence]);

  // Gestion des onglets
  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
    if (newValue === 1) {
      fetchSavedSources();
    }
  };

  // Gestion de la recherche avec mise à jour des recommandations
  const handleSearchWithRecommendations = async (params) => {
    const result = await handleSearch(params);
    
    if (result.success && result.metadata?.providers) {
      setProviderRecommendations(result.metadata.providers);
      
      if (searchFormRef.current && typeof searchFormRef.current.updateProviderRecommendations === 'function') {
        searchFormRef.current.updateProviderRecommendations(result.metadata.providers);
      }
      
      setActiveTab(1); // Basculer vers l'onglet résultats
    }
    
    return result;
  };

  // Gestion du changement de mode avec recommandations
  const handleModeChangeWithRecommendations = async (newMode) => {
    const providers = await handleModeChange(newMode);
    
    if (providers && providers.length > 0 && searchFormRef.current) {
      searchFormRef.current.updateProviderRecommendations(providers);
    }
  };

  // Gestion de la sauvegarde des sources
  const handleSaveSources = async (selectedSources, result) => {
    fetchSavedSources();
  };

  // Navigation
  const handleReturnToCompetences = () => {
    navigate('/');
  };
  
  const goToAnalyzePhase = () => {
    navigate(`/webSearch/${competenceId}`);
  };

  const goToSemanticAnalysis = () => {
    navigate(`/intent-analysis/${competenceId}`);
  };

  const handleRefresh = () => {
    window.location.reload();
  };
  
  // Affichage conditionnel pour les erreurs
  if (loading && !competence && !competenceError) {
    return (
      <div className="flex justify-center items-center h-64">
        <CircularProgress />
        <Typography variant="body1" className="ml-2">
          Chargement de la compétence...
        </Typography>
      </div>
    );
  }
  
  if (competenceError) {
    return (
      <div className="w-full p-6">
        <style>{harmonizedStyles}</style>
        <Alert severity="error" className="mb-6">
          {competenceError}
        </Alert>
        <div className="text-center my-8">
          <Button 
            variant="contained" 
            color="primary" 
            startIcon={<ArrowLeft />}
            onClick={handleReturnToCompetences}
          >
            Retourner à la liste des objectifs
          </Button>
        </div>
      </div>
    );
  }
  
  return (
    <div className="w-full min-h-screen bg-gradient-to-b from-blue-50 via-purple-50 to-white">
      <style>{harmonizedStyles}</style>
      
      <div className="container mx-auto px-4 py-8">
        <InspirationBreadcrumb 
          competenceId={competenceId}
          currentPage="search"
        />

        {/* En-tête de l'objectif */}
        <ObjectiveHeader 
          competence={competence}
          autoConfigApplied={autoConfigApplied}
          geoAnalysisLoading={geoAnalysisLoading}
          preferencesLoaded={preferencesLoaded}
          onSemanticAnalysis={goToSemanticAnalysis}
          onRefresh={handleRefresh}
        />

        {/* Affichage de l'analyse géographique */}
        <GeographicAnalysisDisplay 
          analysis={geographicAnalysis}
          autoConfigApplied={autoConfigApplied}
          onApplyConfig={handleApplyGeographicConfig}
        />

        {/* Onglets de navigation */}
        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
          <Tabs 
            value={activeTab} 
            onChange={handleTabChange} 
            aria-label="onglets de recherche"
            variant="fullWidth"
            sx={{
              '& .MuiTab-root': { py: 1.5 },
              '& .Mui-selected': { 
                color: '#7c3aed !important',
                fontWeight: 700
              },
              '& .MuiTabs-indicator': { 
                backgroundColor: '#7c3aed' 
              }
            }}
          >
            <Tab 
              icon={<Globe />} 
              iconPosition="start" 
              label="Rechercher 360°" 
              sx={tabStyle}
            />
            <Tab 
              icon={<Filter />} 
              iconPosition="start" 
              label={`Résultats ${sources.length || savedSources.length ? `(${sources.length || savedSources.length})` : ''}`} 
              sx={tabStyle}
            />
          </Tabs>
        </Box>

        {/* Affichage des erreurs */}
        {error && (
          <Alert severity="error" className="mb-6">
            {error}
          </Alert>
        )}
        
        {/* Contenu de l'onglet 1: Rechercher */}
        {activeTab === 0 && (
          <div>
            <SourceForm
              ref={searchFormRef}
              onSearch={handleSearchWithRecommendations}
              initialValues={{
                ...searchParams,
                objective: competence?.title || ""
              }}
              userAuthToken={token}
              competenceId={competenceId}
              loading={loading}
              onModeChange={handleModeChangeWithRecommendations}
              hideObjectiveField={true}
              suggestedQuery={suggestedQuery}
              semanticAnalysis={semanticAnalysis}
              geographicAnalysis={geographicAnalysis}
              autoConfigApplied={autoConfigApplied}
              userPreferences={userPreferences}
            />
          </div>
        )}
        
        {/* Contenu de l'onglet 2: Résultats */}
        {activeTab === 1 && (
          <div>
            {sources.length > 0 ? (
              <SourceResults
                sources={sources}
                metadata={metadata}
                loading={loading}
                error={error}
                onSave={handleSaveSources}
                userAuthToken={token}
                userId={auth.currentUser?.uid}
                competenceId={competenceId}
                fromSuggestedQuery={suggestedQuery ? true : false}
              />
            ) : loadingSaved ? (
              <div className="flex justify-center my-12">
                <CircularProgress />
              </div>
            ) : savedSources.length > 0 ? (
              <div className="bg-white rounded-xl shadow-md p-6">
                <SourceResults
                  sources={savedSources}
                  metadata={{ 
                    title: "Sources sauvegardées",
                    sessionId: savedSources[0]?.sessionId, 
                    sourceId: savedSources[0]?.sessionId,   
                    category: metadata.category,
                    count: savedSources.length,
                    timestamp: metadata.timestamp,
                    query: metadata.query,
                    recovery: metadata.recovery,
                    accessibility: metadata.accessibility,
                    fromSuggestedQuery: suggestedQuery ? true : false
                  }}
                  onSave={handleSaveSources}
                  onDelete={handleDeleteSavedSource}
                  userAuthToken={token}
                  userId={auth.currentUser?.uid}
                  competenceId={competenceId}
                />
              </div>
            ) : (
              <Alert severity="info" className="my-8">
                {suggestedQuery ? (
                  <>
                    La recherche avec la requête suggérée <strong>"{suggestedQuery}"</strong> est en cours de préparation. 
                    Veuillez patienter ou cliquez sur "Rechercher" dans l'onglet précédent pour lancer la recherche manuellement.
                  </>
                ) : (
                  "Vous n'avez pas encore effectué de recherche ou sauvegardé de sources. Utilisez l'onglet 'Rechercher 360°' pour trouver et sauvegarder des sources."
                )}
              </Alert>
            )}
          </div>
        )}
        
        {/* Affichage des informations de récupération d'URLs */}
        <RecoveryInfoDisplay metadata={metadata} />
        
        {/* Affichage de l'analyse sémantique */}
        <SemanticAnalysisDisplay semanticAnalysis={semanticAnalysis} />

        {/* Bouton de navigation vers la phase suivante */}
        {(sources.length > 0 || savedSources.length > 0) && (
          <div className="mt-8 text-center">
            <Button
              variant="contained"
              size="large"
              endIcon={<ChevronRight />}
              onClick={goToAnalyzePhase}
              sx={{
                background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #059669 0%, #047857 100%)',
                },
                textTransform: 'none',
                fontWeight: 600,
                px: 6,
                py: 2,
                fontSize: '1.1rem'
              }}
            >
              Continuer vers la Transformation
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}